<div class="topic-share-container">
  <header class="topic-share-header">
    <h1>📊 Topic Share 3</h1>
    <p>Hybrid approach: Shared entity list (2+ domains) with comprehensive discovery</p>
  </header>

  <div class="topic-share-section">
    <div class="form-container">
      <div class="form-group">
        <label for="topic-input">Topic:</label>
        <input
          id="topic-input"
          type="text"
          [(ngModel)]="topic"
          placeholder="e.g., project management"
          class="topic-input"
          [disabled]="loading"
          (keyup.enter)="discoverCoreKeyword()"
        />
      </div>

      <div class="defaults-indicator">
        <span class="indicator-icon">🌍</span>
        <span class="indicator-text">
          Location: United States ({{ defaultLocationCode }}) | Language: {{ defaultLanguageName }}
        </span>
      </div>

      <button 
        (click)="discoverCoreKeyword()" 
        [disabled]="loading || !topic.trim()"
        class="discover-button"
      >
        <span *ngIf="!loading">Discover ranking domains</span>
        <span *ngIf="loading">Discovering...</span>
      </button>
    </div>

    <div *ngIf="error" class="error-message">
      <strong>Error:</strong> {{ error }}
    </div>

    <!-- Core Keyword Result -->
    <div *ngIf="result && metadata" class="results-container">
      <div class="result-card">
        <div class="result-header">
          <h2>Core Keyword Result</h2>
          <div class="metadata-badge" [class.from-database]="metadata.dataSource === 'database'" [class.from-api]="metadata.dataSource === 'api'">
            <span class="badge-icon">{{ metadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ metadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="metadata.lastUpdated" class="badge-date">
              ({{ formatDate(metadata.lastUpdated) }})
            </span>
          </div>
        </div>

        <div class="result-content">
          <div class="result-item">
            <label>Topic:</label>
            <span class="result-value">{{ result.topic }}</span>
          </div>
          <div *ngIf="result.topicMetrics" class="result-item">
            <label>Topic Metrics:</label>
            <div class="metrics-container">
              <span class="metric-item">
                <strong>Search Volume:</strong> {{ result.topicMetrics.searchVolume.toLocaleString() }}
              </span>
              <span class="metric-item">
                <strong>CPC:</strong> ${{ result.topicMetrics.cpc.toFixed(2) }}
              </span>
              <span class="metric-item">
                <strong>Competition:</strong> {{ (result.topicMetrics.competition * 100).toFixed(1) }}%
                <span *ngIf="result.topicMetrics.competitionLevel" class="competition-level">
                  ({{ result.topicMetrics.competitionLevel }})
                </span>
              </span>
            </div>
          </div>
          <div class="result-item">
            <label>Core Keyword:</label>
            <span class="result-value core-keyword">{{ result.coreKeyword }}</span>
          </div>
          <div *ngIf="result.coreKeywordMetrics && result.coreKeyword !== result.topic" class="result-item">
            <label>Core Keyword Metrics:</label>
            <div class="metrics-container">
              <span class="metric-item">
                <strong>Search Volume:</strong> {{ result.coreKeywordMetrics.searchVolume.toLocaleString() }}
              </span>
              <span class="metric-item">
                <strong>CPC:</strong> ${{ result.coreKeywordMetrics.cpc.toFixed(2) }}
              </span>
              <span class="metric-item">
                <strong>Competition:</strong> {{ (result.coreKeywordMetrics.competition * 100).toFixed(1) }}%
                <span *ngIf="result.coreKeywordMetrics.competitionLevel" class="competition-level">
                  ({{ result.coreKeywordMetrics.competitionLevel }})
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SERP Results Section -->
    <div *ngIf="loadingSerp" class="loading-indicator">
      <span>Loading ranking domains...</span>
    </div>

    <div *ngIf="serpResults.topic || serpResults.coreKeyword" class="serp-results-container">
      <div class="serp-results-header">
        <h2>Ranking Domains</h2>
        <div *ngIf="totalCost > 0 || historicalTotalCost > 0" class="total-cost">
          Run Cost: ${{ totalCost.toFixed(4) }}
          <div *ngIf="historicalTotalCost > 0" class="historical-cost-inline">
            Historical (cached): ${{ historicalTotalCost.toFixed(4) }}
          </div>
        </div>
      </div>
      
      <div class="serp-columns">
        <div *ngIf="serpResults.topic" class="serp-column">
          <div class="serp-section">
            <div class="serp-header">
              <h3>For: "{{ searchTopic }}"</h3>
              <div *ngIf="serpMetadata.topic" class="metadata-badge" 
                   [class.from-database]="serpMetadata.topic.dataSource === 'database'" 
                   [class.from-api]="serpMetadata.topic.dataSource === 'api'">
                <span class="badge-icon">{{ serpMetadata.topic.dataSource === 'database' ? '💾' : '🌐' }}</span>
                <span class="badge-text">
                  {{ serpMetadata.topic.dataSource === 'database' ? 'From Database' : 'From API' }}
                </span>
                <span *ngIf="serpMetadata.topic.lastUpdated" class="badge-date">
                  ({{ formatDate(serpMetadata.topic.lastUpdated) }})
                </span>
              </div>
            </div>
            <div class="serp-results-list">
              <div *ngFor="let result of (showAllSerpResults.topic ? serpResults.topic.results : serpResults.topic.results.slice(0, serpDisplayLimit))" 
                   class="serp-result-item">
                <div class="position-badge">{{ result.position }}</div>
                <div class="result-content">
                  <a [href]="result.url" target="_blank" rel="noopener noreferrer" class="result-title">
                    {{ result.title }}
                  </a>
                  <div class="result-url">{{ result.url }}</div>
                  <div class="result-domain">{{ result.domain }}</div>
                </div>
              </div>
              <div *ngIf="serpResults.topic.results.length > serpDisplayLimit" class="load-more-container">
                <button (click)="showAllSerpResults.topic = !showAllSerpResults.topic" class="load-more-button">
                  <span *ngIf="!showAllSerpResults.topic">Show All ({{ serpResults.topic.results.length - serpDisplayLimit }} more)</span>
                  <span *ngIf="showAllSerpResults.topic">Show Less</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="serpResults.coreKeyword && !isTopicSameAsCoreKeyword" class="serp-column">
          <div class="serp-section">
            <div class="serp-header">
              <h3>For: "{{ result?.coreKeyword }}"</h3>
              <div *ngIf="serpMetadata.coreKeyword" class="metadata-badge" 
                   [class.from-database]="serpMetadata.coreKeyword.dataSource === 'database'" 
                   [class.from-api]="serpMetadata.coreKeyword.dataSource === 'api'">
                <span class="badge-icon">{{ serpMetadata.coreKeyword.dataSource === 'database' ? '💾' : '🌐' }}</span>
                <span class="badge-text">
                  {{ serpMetadata.coreKeyword.dataSource === 'database' ? 'From Database' : 'From API' }}
                </span>
                <span *ngIf="serpMetadata.coreKeyword.lastUpdated" class="badge-date">
                  ({{ formatDate(serpMetadata.coreKeyword.lastUpdated) }})
                </span>
              </div>
            </div>
            <div class="serp-results-list">
              <div *ngFor="let result of (showAllSerpResults.coreKeyword ? serpResults.coreKeyword.results : serpResults.coreKeyword.results.slice(0, serpDisplayLimit))" 
                   class="serp-result-item">
                <div class="position-badge">{{ result.position }}</div>
                <div class="result-content">
                  <a [href]="result.url" target="_blank" rel="noopener noreferrer" class="result-title">
                    {{ result.title }}
                  </a>
                  <div class="result-url">{{ result.url }}</div>
                  <div class="result-domain">{{ result.domain }}</div>
                </div>
              </div>
              <div *ngIf="serpResults.coreKeyword.results.length > serpDisplayLimit" class="load-more-container">
                <button (click)="showAllSerpResults.coreKeyword = !showAllSerpResults.coreKeyword" class="load-more-button">
                  <span *ngIf="!showAllSerpResults.coreKeyword">Show All ({{ serpResults.coreKeyword.results.length - serpDisplayLimit }} more)</span>
                  <span *ngIf="showAllSerpResults.coreKeyword">Show Less</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Domains Section -->
    <div *ngIf="serpResults.topic || serpResults.coreKeyword" class="custom-domains-container">
      <div class="custom-domains-header">
        <h2>Custom Domains to Include</h2>
        <span class="custom-domains-subtitle">
          Add domains that don't appear in SERP results but you want to include in the coverage analysis.
        </span>
      </div>

      <div class="custom-domains-info">
        <span class="info-icon">💡</span>
        <span class="info-text">
          By default, Topic Share only analyzes domains that rank in the top 10 for your topic.
          Add your domain or competitor domains here if they don't rank yet but you want to compare their entity coverage.
        </span>
      </div>

      <div class="custom-domains-input">
        <input
          type="text"
          [(ngModel)]="newCustomDomain"
          placeholder="e.g., yoursite.com or competitor.com"
          class="domain-input"
          (keyup.enter)="addCustomDomain()"
        />
        <button 
          (click)="addCustomDomain()" 
          [disabled]="!newCustomDomain.trim()"
          class="add-domain-button"
        >
          + Add Domain
        </button>
      </div>

      <div *ngIf="customDomains.length > 0" class="custom-domains-list">
        <div class="domains-label">Custom domains to analyze:</div>
        <div class="domain-tags">
          <span *ngFor="let domain of customDomains" class="domain-tag custom">
            {{ domain }}
            <button class="remove-domain-btn" (click)="removeCustomDomain(domain)" title="Remove domain">×</button>
          </span>
        </div>
      </div>

      <div *ngIf="getUniqueDomains().length > 0" class="all-domains-summary">
        <div class="domains-label">All domains that will be analyzed ({{ getUniqueDomains().length }}):</div>
        <div class="domain-tags">
          <span *ngFor="let domain of getUniqueDomains()" 
                class="domain-tag"
                [class.serp]="isDomainFromSerp(domain)"
                [class.custom]="!isDomainFromSerp(domain)">
            {{ domain }}
            <span class="domain-source">{{ isDomainFromSerp(domain) ? '(SERP)' : '(custom)' }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Initial Entity Discovery Section -->
    <div *ngIf="hasSerpResults" class="entity-discovery-container">
      <div class="entity-discovery-header">
        <h2>Step 4: Discover Initial Entities</h2>
        <button 
          (click)="discoverEntities()" 
          [disabled]="loadingEntities || !hasSerpResults"
          class="discover-button"
        >
          <span *ngIf="!loadingEntities">Discover Entities</span>
          <span *ngIf="loadingEntities">Discovering...</span>
        </button>
      </div>

      <div *ngIf="loadingEntities" class="loading-indicator">
        <span>Discovering entities from Related Keywords, Suggestions, and Ranked Keywords...</span>
      </div>

      <div *ngIf="entities.length > 0 && entityCostBreakdown" class="entities-results">
        <div class="section-header">
          <h3>Initial Entity Discovery Results</h3>
          <div *ngIf="entityMetadata" class="metadata-badge" 
               [class.from-database]="entityMetadata.dataSource === 'database'" 
               [class.from-api]="entityMetadata.dataSource === 'api'">
            <span class="badge-icon">{{ entityMetadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ entityMetadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="entityMetadata.lastUpdated" class="badge-date">
              ({{ formatDate(entityMetadata.lastUpdated) }})
            </span>
          </div>
        </div>

        <div class="cost-breakdown-section">
          <h3>Cost Breakdown</h3>
          <div class="cost-details">
            <div class="cost-item">
              <span class="cost-label">Related Keywords:</span>
              <span class="cost-value">${{ getRunCostByDataSource((entityCostBreakdown.relatedKeywords || 0), entityMetadata?.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item">
              <span class="cost-label">Suggestions:</span>
              <span class="cost-value">${{ getRunCostByDataSource((entityCostBreakdown.suggestions || 0), entityMetadata?.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item">
              <span class="cost-label">Ranked Keywords:</span>
              <span class="cost-value">${{ getRunCostByDataSource((entityCostBreakdown.rankedKeywords || 0), entityMetadata?.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item cost-total">
              <span class="cost-label">Total:</span>
              <span class="cost-value">${{ getRunCostByDataSource(entityCostBreakdown.total, entityMetadata?.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item" *ngIf="getHistoricalCostByDataSource(entityCostBreakdown.total, entityMetadata?.dataSource) > 0">
              <span class="cost-label">Historical (cached):</span>
              <span class="cost-value">${{ getHistoricalCostByDataSource(entityCostBreakdown.total, entityMetadata?.dataSource).toFixed(4) }}</span>
            </div>
          </div>
        </div>

        <div *ngIf="entityStats" class="stats-section">
          <h3>Statistics</h3>
          <div class="stats-details">
            <div class="stat-item">
              <span class="stat-label">Total Entities:</span>
              <span class="stat-value">{{ entityStats.totalEntities }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Related:</span>
              <span class="stat-value">{{ entityStats.fromRelated || 0 }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Suggestions:</span>
              <span class="stat-value">{{ entityStats.fromSuggestions || 0 }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Ranked Keywords:</span>
              <span class="stat-value">{{ entityStats.fromRankedKeywords || 0 }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Unique Entities:</span>
              <span class="stat-value">{{ entityStats.uniqueEntities }}</span>
            </div>
          </div>
        </div>

        <div class="info-note">
          <strong>Note:</strong> Ranked keywords are filtered by: min search volume >= 10, top 100 by position
        </div>

        <!-- Entities Table -->
        <div class="entities-table-container">
          <div class="table-header">
            <h3>Discovered Entities ({{ entities.length }} total)</h3>
            <button 
              *ngIf="hasMoreEntities()"
              (click)="toggleShowAllEntities()" 
              class="toggle-button"
            >
              {{ showAllEntities ? 'Show Less' : 'Show All' }}
            </button>
          </div>
          <table class="entities-table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Similarity Score</th>
                <th>Search Volume</th>
                <th>Keyword Difficulty</th>
                <th>CPC</th>
                <th>Sources</th>
                <th>Ranked From Pages</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let entity of getDisplayedEntities()">
                <td class="entity-keyword">
                  <strong>{{ entity.keyword }}</strong>
                  <span *ngIf="entity.coreKeyword && entity.coreKeyword !== entity.keyword" class="core-keyword-badge">
                    (core: {{ entity.coreKeyword }})
                  </span>
                </td>
                <td>
                  <span class="similarity-score" [class]="getSimilarityColorClass(entity.similarityScore)">
                    {{ formatSimilarity(entity.similarityScore) }}
                  </span>
                </td>
                <td>{{ entity.searchVolume.toLocaleString() }}</td>
                <td>{{ entity.keywordDifficulty }}</td>
                <td>${{ (entity.cpc || 0).toFixed(2) }}</td>
                <td>
                  <div class="sources-badges">
                    <span *ngFor="let source of entity.sources" class="source-badge" [class]="'badge-' + source">
                      {{ source === 'related' ? 'Related' : source === 'suggestions' ? 'Suggestions' : source === 'ranked_keywords' ? 'Ranked' : 'Ranked (Filtered)' }}
                    </span>
                  </div>
                </td>
                <td class="ranked-pages">
                  <div *ngIf="getRankedFromPagesAsObjects(entity.rankedFromPages).length > 0" class="pages-list">
                    <a *ngFor="let page of getRankedFromPagesAsObjects(entity.rankedFromPages).slice(0, 3)" 
                       [href]="page.url" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       class="page-link"
                       [title]="page.title">
                      {{ page.title || page.url }}
                    </a>
                    <span *ngIf="getRankedFromPagesAsObjects(entity.rankedFromPages).length > 3" class="more-pages">
                      +{{ getRankedFromPagesAsObjects(entity.rankedFromPages).length - 3 }} more
                    </span>
                  </div>
                  <div *ngIf="getRankedFromPagesAsStrings(entity.rankedFromPages).length > 0" class="pages-list">
                    <a *ngFor="let url of getRankedFromPagesAsStrings(entity.rankedFromPages).slice(0, 3)" 
                       [href]="url" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       class="page-link">
                      {{ url }}
                    </a>
                    <span *ngIf="getRankedFromPagesAsStrings(entity.rankedFromPages).length > 3" class="more-pages">
                      +{{ getRankedFromPagesAsStrings(entity.rankedFromPages).length - 3 }} more
                    </span>
                  </div>
                  <span *ngIf="isRankedFromPagesEmpty(entity.rankedFromPages)" class="no-pages">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Entity Filter Section -->
    <div *ngIf="entities.length > 0" class="entity-filter-container">
      <div class="entity-filter-header">
        <h2>Step 5: Filter to Core Keywords</h2>
        <button 
          (click)="filterEntities()" 
          [disabled]="loadingFilter || !entities.length"
          class="filter-button"
        >
          <span *ngIf="!loadingFilter">Filter to Core Keywords</span>
          <span *ngIf="loadingFilter">Filtering...</span>
        </button>
      </div>

      <div *ngIf="loadingFilter" class="loading-indicator">
        <span>Filtering entities to core keywords...</span>
      </div>

      <div *ngIf="filteredCoreKeywords.length > 0" class="filter-results-container">
        <div class="filter-results-header">
          <h3>Filtered Core Keywords ({{ filteredCoreKeywords.length }})</h3>
        </div>

        <div class="filtered-keywords-list">
          <div *ngFor="let keyword of filteredCoreKeywords; let i = index" class="filtered-keyword-item">
            <div class="keyword-number">{{ i + 1 }}</div>
            <div class="keyword-content">
              <strong>{{ keyword }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtered Entity Discovery Section -->
    <div *ngIf="filteredCoreKeywords.length > 0" class="filtered-entity-discovery-container">
      <div class="filtered-entity-discovery-header">
        <h2>Step 6: Discover Filtered Entities</h2>
        <button 
          (click)="discoverFilteredEntities()" 
          [disabled]="loadingFilteredEntities || !filteredCoreKeywords.length"
          class="discover-button"
        >
          <span *ngIf="!loadingFilteredEntities">Discover More Entities + Ranking Pages</span>
          <span *ngIf="loadingFilteredEntities">Discovering...</span>
        </button>
      </div>

      <div *ngIf="loadingFilteredEntities" class="loading-indicator">
        <span>Discovering entities using filtered core keywords...</span>
      </div>

      <div *ngIf="filteredEntities.length > 0 && filteredEntityCostBreakdown" class="filtered-entities-results">
        <div class="section-header">
          <h3>Filtered Entity Discovery Results</h3>
          <div *ngIf="filteredEntityMetadata" class="metadata-badge"
               [class.from-database]="filteredEntityMetadata.dataSource === 'database'"
               [class.from-api]="filteredEntityMetadata.dataSource === 'api'">
            <span class="badge-icon">{{ filteredEntityMetadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ filteredEntityMetadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="filteredEntityMetadata.lastUpdated" class="badge-date">
              ({{ formatDate(filteredEntityMetadata.lastUpdated) }})
            </span>
          </div>
        </div>
        <div class="cost-breakdown-section">
          <h3>Cost Breakdown</h3>
          <div class="cost-details">
            <div class="cost-item">
              <span class="cost-label">Ranked Keywords (Filtered):</span>
              <span class="cost-value">${{ getRunCostByDataSource((filteredEntityCostBreakdown.rankedKeywordsFiltered || 0), filteredEntityMetadata?.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item cost-total">
              <span class="cost-label">Total:</span>
              <span class="cost-value">${{ getRunCostByDataSource(filteredEntityCostBreakdown.total, filteredEntityMetadata?.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item" *ngIf="getHistoricalCostByDataSource(filteredEntityCostBreakdown.total, filteredEntityMetadata?.dataSource) > 0">
              <span class="cost-label">Historical (cached):</span>
              <span class="cost-value">${{ getHistoricalCostByDataSource(filteredEntityCostBreakdown.total, filteredEntityMetadata?.dataSource).toFixed(4) }}</span>
            </div>
          </div>
        </div>

        <div *ngIf="filteredEntityStats" class="stats-section">
          <h3>Statistics</h3>
          <div class="stats-details">
            <div class="stat-item">
              <span class="stat-label">Total Entities:</span>
              <span class="stat-value">{{ filteredEntityStats.totalEntities.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Ranked Keywords (Filtered):</span>
              <span class="stat-value">{{ (filteredEntityStats.fromRankedKeywordsFiltered || 0).toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Unique Entities:</span>
              <span class="stat-value">{{ filteredEntityStats.uniqueEntities.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Domains Analyzed:</span>
              <span class="stat-value">{{ filteredEntityStats.domainsAnalyzed }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Shared Entity List Section -->
    <div *ngIf="entities.length > 0 && filteredEntities.length > 0" class="shared-entity-list-container">
      <div class="shared-entity-list-header">
        <h2>Step 7: Build Shared Entity List</h2>
        <button 
          (click)="buildSharedEntityList()" 
          [disabled]="loadingSharedEntities || !entities.length || !filteredEntities.length"
          class="discover-button"
        >
          <span *ngIf="!loadingSharedEntities">Build Shared Entity List (2+ domains)</span>
          <span *ngIf="loadingSharedEntities">Building...</span>
        </button>
      </div>

      <div *ngIf="loadingSharedEntities" class="loading-indicator">
        <span>Building shared entity list (filtering to keywords in 2+ domains)...</span>
      </div>

      <div *ngIf="sharedEntities.length > 0 && sharedEntityMetadata" class="shared-entities-results">
        <div class="section-header">
          <h3>Shared Entity List</h3>
          <div class="metadata-badge" [class.from-database]="sharedEntityMetadata.dataSource === 'database'" [class.from-api]="sharedEntityMetadata.dataSource === 'api'">
            <span class="badge-icon">{{ sharedEntityMetadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ sharedEntityMetadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="sharedEntityMetadata.lastUpdated" class="badge-date">
              ({{ formatDate(sharedEntityMetadata.lastUpdated) }})
            </span>
          </div>
        </div>

        <div *ngIf="sharedEntityCostBreakdown" class="cost-breakdown-section">
          <h3>Cost Breakdown</h3>
          <div class="cost-details">
            <div class="cost-item">
              <span class="cost-label">Initial Discovery:</span>
              <span class="cost-value">${{ getRunCostByDataSource((sharedEntityCostBreakdown.initialDiscovery || 0), sharedEntityMetadata.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item">
              <span class="cost-label">Filtered Discovery:</span>
              <span class="cost-value">${{ getRunCostByDataSource((sharedEntityCostBreakdown.filteredDiscovery || 0), sharedEntityMetadata.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item cost-total">
              <span class="cost-label">Total:</span>
              <span class="cost-value">${{ getRunCostByDataSource(sharedEntityCostBreakdown.total, sharedEntityMetadata.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item" *ngIf="getHistoricalCostByDataSource(sharedEntityCostBreakdown.total, sharedEntityMetadata.dataSource) > 0">
              <span class="cost-label">Historical (cached):</span>
              <span class="cost-value">${{ getHistoricalCostByDataSource(sharedEntityCostBreakdown.total, sharedEntityMetadata.dataSource).toFixed(4) }}</span>
            </div>
          </div>
        </div>

        <div *ngIf="sharedEntityStats" class="stats-section">
          <h3>Statistics</h3>
          <div class="stats-details">
            <div class="stat-item">
              <span class="stat-label">Total Discovered:</span>
              <span class="stat-value">{{ sharedEntityStats.totalDiscovered.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">After Deduplication:</span>
              <span class="stat-value">{{ sharedEntityStats.afterDeduplication.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">After Shared Filter (2+ domains):</span>
              <span class="stat-value">{{ sharedEntityStats.afterSharedFilter.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Initial Discovery:</span>
              <span class="stat-value">{{ sharedEntityStats.fromInitialDiscovery.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">From Filtered Discovery:</span>
              <span class="stat-value">{{ sharedEntityStats.fromFilteredDiscovery.toLocaleString() }}</span>
            </div>
          </div>
        </div>

        <div class="entities-table-container">
          <div class="table-header">
            <h3>Shared Entities ({{ sharedEntities.length }} total)</h3>
            <button 
              *ngIf="sharedEntities.length > 50"
              (click)="showAllSharedEntities = !showAllSharedEntities" 
              class="load-more-button"
            >
              <span *ngIf="!showAllSharedEntities">Show All ({{ sharedEntities.length - 50 }} more)</span>
              <span *ngIf="showAllSharedEntities">Show Less</span>
            </button>
          </div>
          <table class="entities-table">
            <thead>
              <tr>
                <th>Keyword</th>
                <th>Domain Count</th>
                <th>Domains</th>
                <th>Similarity Score</th>
                <th>Search Volume</th>
                <th>Sources</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let entity of (showAllSharedEntities ? sharedEntities : sharedEntities.slice(0, 50))">
                <td class="entity-keyword">
                  <strong>{{ entity.keyword }}</strong>
                  <span *ngIf="entity.coreKeyword && entity.coreKeyword !== entity.keyword" class="core-keyword-badge">
                    (core: {{ entity.coreKeyword }})
                  </span>
                </td>
                <td>
                  <span class="domain-count-badge">{{ entity.domainCount }}</span>
                </td>
                <td>
                  <div class="domains-list">
                    <span *ngFor="let domain of entity.domains.slice(0, 3)" class="domain-badge">{{ domain }}</span>
                    <span *ngIf="entity.domains.length > 3" class="more-domains">+{{ entity.domains.length - 3 }} more</span>
                  </div>
                </td>
                <td>
                  <span class="similarity-score" [class]="getSimilarityColorClass(entity.similarityScore)">
                    {{ formatSimilarity(entity.similarityScore) }}
                  </span>
                </td>
                <td>{{ entity.searchVolume.toLocaleString() }}</td>
                <td>
                  <div class="sources-badges">
                    <span *ngFor="let source of entity.sources" class="source-badge" [class]="'badge-' + source">
                      {{ source === 'related' ? 'Related' : source === 'suggestions' ? 'Suggestions' : source === 'ranked_keywords' ? 'Ranked' : 'Ranked (Filtered)' }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Domain Coverage Section -->
    <div *ngIf="sharedEntities.length > 0" class="domain-coverage-container">
      <div class="domain-coverage-header">
        <h2>Step 8: Check Domain Coverage</h2>
        <button 
          (click)="checkDomainCoverage()" 
          [disabled]="loadingDomainCoverage || !sharedEntities.length"
          class="coverage-button"
        >
          <span *ngIf="!loadingDomainCoverage">Check Domain Coverage</span>
          <span *ngIf="loadingDomainCoverage">Checking...</span>
        </button>
      </div>

      <div *ngIf="loadingDomainCoverage" class="loading-indicator">
        <span>Checking domain coverage against shared entity list...</span>
      </div>

      <div *ngIf="domainCoverageResults.length > 0 && domainCoverageMetadata" class="coverage-results">
        <div class="section-header">
          <h3>Domain Coverage Summary</h3>
          <div class="metadata-badge" [class.from-database]="domainCoverageMetadata.dataSource === 'database'" [class.from-api]="domainCoverageMetadata.dataSource === 'api'">
            <span class="badge-icon">{{ domainCoverageMetadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ domainCoverageMetadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="domainCoverageMetadata.lastUpdated" class="badge-date">
              ({{ formatDate(domainCoverageMetadata.lastUpdated) }})
            </span>
          </div>
        </div>

        <div *ngIf="domainCoverageCostBreakdown" class="cost-breakdown-section">
          <h3>Cost Breakdown</h3>
          <div class="cost-details">
            <div class="cost-item">
              <span class="cost-label">Ranked Keywords:</span>
              <span class="cost-value">${{ getRunCostByDataSource((domainCoverageCostBreakdown.rankedKeywords || 0), domainCoverageMetadata.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item cost-total">
              <span class="cost-label">Total:</span>
              <span class="cost-value">${{ getRunCostByDataSource(domainCoverageCostBreakdown.total, domainCoverageMetadata.dataSource).toFixed(4) }}</span>
            </div>
            <div class="cost-item" *ngIf="getHistoricalCostByDataSource(domainCoverageCostBreakdown.total, domainCoverageMetadata.dataSource) > 0">
              <span class="cost-label">Historical (cached):</span>
              <span class="cost-value">${{ getHistoricalCostByDataSource(domainCoverageCostBreakdown.total, domainCoverageMetadata.dataSource).toFixed(4) }}</span>
            </div>
          </div>
        </div>

        <div *ngIf="domainCoverageStats" class="stats-section">
          <h3>Statistics</h3>
          <div class="stats-details">
            <div class="stat-item">
              <span class="stat-label">Total Entities:</span>
              <span class="stat-value">{{ domainCoverageStats.totalEntities.toLocaleString() }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Total Domains:</span>
              <span class="stat-value">{{ domainCoverageStats.totalDomains }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Total API Calls:</span>
              <span class="stat-value">{{ domainCoverageStats.totalApiCalls }}</span>
            </div>
          </div>
        </div>

        <div class="domains-table-container">
          <table class="domains-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Entities Covered</th>
                <th>Total Keywords Ranked</th>
                <th>Coverage %</th>
                <th>URLs Ranking</th>
                <th>Ranking URLs</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let domain of domainCoverageResults">
                <td class="domain-name"><strong>{{ domain.domain }}</strong></td>
                <td>{{ domain.entitiesCovered.toLocaleString() }}</td>
                <td>{{ domain.totalKeywordsRanked.toLocaleString() }}</td>
                <td>{{ domain.coveragePercentage.toFixed(1) }}%</td>
                <td>{{ domain.urls.length }}</td>
                <td class="urls-list">
                  <div *ngFor="let url of domain.urls.slice(0, 5)" class="url-item">
                    <a [href]="url.url" target="_blank" rel="noopener noreferrer" class="url-link">
                      {{ url.title || url.url }}
                    </a>
                    <span class="url-meta">(pos: {{ url.position }}, {{ url.entitiesCount }} entities)</span>
                  </div>
                  <div *ngIf="domain.urls.length > 5" class="more-urls">
                    +{{ domain.urls.length - 5 }} more URLs
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Total Cost Summary -->
    <div *ngIf="totalCost > 0 || historicalTotalCost > 0" class="total-cost-summary">
      <h3>Run Cost Summary</h3>
      <div class="total-cost-value">${{ totalCost.toFixed(4) }}</div>
      <div *ngIf="historicalTotalCost > 0" class="historical-cost-inline">
        Historical Cost (cached, not billed this run): ${{ historicalTotalCost.toFixed(4) }}
      </div>
    </div>
  </div>
</div>

